/**
 * Scroll restoration and position management across the exhibition and artwork viewer.
 */

const SCROLL_STORAGE_KEY = 'schz-scroll-depth';

/**
 * Configure browser scroll restoration to manual so React cleanly orchestrates
 * scroll positions across SPA routes.
 */
export function initScrollRestoration(): void {
  if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
}

/**
 * Record current scroll position on the main exhibition grid.
 */
export function saveMainPageScroll(): void {
  if (typeof window === 'undefined') return;
  const y = window.scrollY || document.documentElement.scrollTop || 0;
  sessionStorage.setItem(SCROLL_STORAGE_KEY, y.toString());
}

/**
 * Retrieve saved scroll level for the main exhibition grid.
 */
export function getMainPageScroll(): number {
  if (typeof window === 'undefined') return 0;
  const saved = sessionStorage.getItem(SCROLL_STORAGE_KEY);
  if (saved !== null) {
    const val = parseInt(saved, 10);
    return isNaN(val) ? 0 : Math.max(0, val);
  }
  return 0;
}

/**
 * Restore the exact scroll level when returning to the main exhibition grid.
 */
export function restoreMainPageScroll(): void {
  if (typeof window === 'undefined') return;
  const targetY = getMainPageScroll();
  if (targetY > 0) {
    const applyScroll = () => {
      window.scrollTo({ top: targetY, left: 0, behavior: 'instant' as ScrollBehavior });
      document.documentElement.scrollTop = targetY;
      document.body.scrollTop = targetY;
    };

    applyScroll();
    requestAnimationFrame(applyScroll);
    setTimeout(applyScroll, 40);
    setTimeout(applyScroll, 120);
  }
}

/**
 * Instantly reset scroll to top (0, 0) so headers and 'Back to Exhibition'
 * navigation controls are fully visible.
 */
export function scrollToTopInstant(): void {
  if (typeof window === 'undefined') return;
  const applyTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  applyTop();
  requestAnimationFrame(applyTop);
  setTimeout(applyTop, 40);
  setTimeout(applyTop, 100);
}
